import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Search, X, Layers, Eye, CheckCircle2, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { getSpCategories, createSpCategory, updateSpCategory, deleteSpCategory, toggleSpCategoryStatus, checkSpCategoryName, clearSpCategoryNameCheck, downloadSpCategorySampleExcel, bulkUploadSpCategories, checkUsedByDoctors, checkSpCategoryBulkFormat } from '../../redux/masterSlice';
import ErrorPage from '../../components/ErrorPage';
import MasterLabel from '../../components/common/MasterLabel';

const SpecialityCategory = () => {
    const { t } = useTranslation('spcategorymaster');
    const dispatch = useDispatch();
    const { spCategories, spCategoryLoading, spCategoryCreateLoading, spCategoryUpdateLoading, spCategoryDeleteLoading, spCategoryToggleLoading, spCategoryPagination, spCategoryNameCheckLoading, spCategoryNameCheckResult, spCategoryBulkUploadLoading, spCategoryDownloadLoading, error } = useSelector((state) => state.master);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [viewingCategory, setViewingCategory] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        spcategoryName: ''
    });
    const [errors, setErrors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [activeTab, setActiveTab] = useState('active');

    // Bulk upload state
    const [bulkUploadFile, setBulkUploadFile] = useState(null);
    const [bulkUploadResult, setBulkUploadResult] = useState(null);
    const [bulkFileError, setBulkFileError] = useState('');
    const [bulkFormatChecking, setBulkFormatChecking] = useState(false);
    const [bulkFormatValid, setBulkFormatValid] = useState(false);
    const fileInputRef = useRef(null);

    // Debounce search query
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Debounce name check
    const [debouncedNameCheck, setDebouncedNameCheck] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedNameCheck(formData.spcategoryName.trim());
        }, 500);
        return () => clearTimeout(timer);
    }, [formData.spcategoryName]);

    // Check name when it changes
    useEffect(() => {
        if (debouncedNameCheck && debouncedNameCheck.length > 0) {
            if (!editingCategory || (editingCategory && debouncedNameCheck !== editingCategory.spcategoryName)) {
                dispatch(checkSpCategoryName(debouncedNameCheck));
            } else {
                dispatch(clearSpCategoryNameCheck());
            }
        } else {
            dispatch(clearSpCategoryNameCheck());
        }
    }, [debouncedNameCheck, editingCategory, dispatch]);

    // Fetch categories when search/page/tab changes
    useEffect(() => {
        dispatch(getSpCategories({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab
        }));
    }, [dispatch, currentPage, itemsPerPage, debouncedSearchQuery, activeTab]);

    const filteredCategories = spCategories || [];
    const paginatedCategories = filteredCategories;

    // Pagination handler
    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Maximum length constants
    const MAX_SEARCH_LENGTH = 50;
    const MAX_NAME_LENGTH = 50;

    // Validate search input
    const validateSearchInput = (value) => {
        const sanitized = value.replace(/[<>]/g, '');
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            return t('spcategorymaster.search_exceed_limit', `Search term cannot exceed ${MAX_SEARCH_LENGTH} characters`);
        }
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('spcategorymaster.search_only_spaces', 'Search term cannot contain only spaces');
        }
        return '';
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > MAX_SEARCH_LENGTH
            ? sanitized.substring(0, MAX_SEARCH_LENGTH)
            : sanitized;
        setSearchQuery(finalValue);
        const error = validateSearchInput(finalValue);
        setSearchError(error);
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === 'spcategoryName') {
            processedValue = value.replace(/[<>]/g, '');
            if (processedValue.length > MAX_NAME_LENGTH) {
                processedValue = processedValue.substring(0, MAX_NAME_LENGTH);
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.spcategoryName.trim()) {
            newErrors.spcategoryName = t('spcategorymaster.name_required', 'Category name is required');
        } else if (formData.spcategoryName.trim().length < 2) {
            newErrors.spcategoryName = t('spcategorymaster.name_min_length', 'Category name must be at least 2 characters');
        } else if (formData.spcategoryName.trim().length > MAX_NAME_LENGTH) {
            newErrors.spcategoryName = t('spcategorymaster.name_max_length', `Category name cannot exceed ${MAX_NAME_LENGTH} characters`);
        } else if (!/^[A-Za-zÀ-ÿ\s\-']+$/.test(formData.spcategoryName.trim())) {
            newErrors.spcategoryName = t('spcategorymaster.name_invalid_chars', 'Category name can only contain letters, spaces, hyphens, and apostrophes');
        }

        if (formData.spcategoryName.trim() && spCategoryNameCheckResult?.exists) {
            newErrors.spcategoryName = spCategoryNameCheckResult?.message || t('spcategorymaster.name_already_exists', 'This category name already exists');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle add new
    const handleAdd = () => {
        setEditingCategory(null);
        setFormData({ spcategoryName: '' });
        setErrors({});
        dispatch(clearSpCategoryNameCheck());
        setShowModal(true);
    };

    // Handle edit
    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            spcategoryName: category.spcategoryName || ''
        });
        setErrors({});
        dispatch(clearSpCategoryNameCheck());
        setShowModal(true);
    };

    // Helper to refresh list
    const refreshList = (page = currentPage) => {
        dispatch(getSpCategories({
            page,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab
        }));
    };

    // Handle delete
    const handleDelete = async (category) => {
        try {
            // Check if category is used by doctors first
            const usageResult = await dispatch(checkUsedByDoctors({ master: 'specialityCategory', id: category.spcategoryId })).unwrap();
            const isUsed = usageResult?.isUsed;
            const count = usageResult?.count || 0;

            if (isUsed) {
                toast.warn(t('spcategorymaster.delete_used_by_doctors', { name: category.spcategoryName, count, defaultValue: `"${category.spcategoryName}" is currently used by ${count} doctor(s). It cannot be deleted.` }));
                return;
            }

            const result = await Swal.fire({
                title: t('spcategorymaster.delete_title', 'Delete Category'),
                text: t('spcategorymaster.delete_text', { name: category.spcategoryName, defaultValue: `Are you sure you want to delete "${category.spcategoryName}"?` }),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#4ABDBD',
                confirmButtonText: t('spcategorymaster.yes_delete', 'Yes, delete it!'),
                cancelButtonText: t('spcategorymaster.cancel', 'Cancel')
            });

            if (result.isConfirmed) {
                await dispatch(deleteSpCategory(category.spcategoryId)).unwrap();
                toast.success(t('spcategorymaster.deleted_success', 'Category has been deleted successfully.'));
                refreshList();
            }
        } catch (error) {
            toast.warn(error?.error || t('spcategorymaster.delete_failed', 'Failed to delete category.'));
        }
    };

    // Handle toggle status
    const handleStatusToggle = async (category) => {
        const currentStatus = category.status;
        const newStatus = currentStatus === true || currentStatus === 'true' ? 'inactive' : 'active';

        try {
            // Only check doctor usage when deactivating
            let isUsed = false;
            let count = 0;
            if (newStatus === 'inactive') {
                const usageResult = await dispatch(checkUsedByDoctors({ master: 'specialityCategory', id: category.spcategoryId })).unwrap();
                isUsed = usageResult?.isUsed;
                count = usageResult?.count || 0;
            }

            if (isUsed) {
                toast.warn(t('spcategorymaster.deactivate_used_by_doctors', { name: category.spcategoryName, count, defaultValue: `"${category.spcategoryName}" is currently used by ${count} doctor(s). It cannot be deactivated.` }));
                return;
            }

            const result = await Swal.fire({
                title: t('spcategorymaster.status_change_title', 'Change Status'),
                text: t('spcategorymaster.status_change_text', {
                    action: newStatus === 'active' ? t('spcategorymaster.activate', 'activate') : t('spcategorymaster.deactivate', 'deactivate'),
                    name: category.spcategoryName,
                    defaultValue: `Are you sure you want to ${newStatus === 'active' ? 'activate' : 'deactivate'} "${category.spcategoryName}"?`
                }),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('spcategorymaster.confirm', 'Confirm'),
                cancelButtonText: t('spcategorymaster.cancel', 'Cancel'),
            });

            if (!result.isConfirmed) return;

            await dispatch(toggleSpCategoryStatus({
                id: category.spcategoryId
            })).unwrap();

            toast.success(newStatus === 'active'
                    ? t('spcategorymaster.activated_success', 'Category activated successfully.')
                    : t('spcategorymaster.deactivated_success', 'Category deactivated successfully.'));
            refreshList();
        } catch (error) {
            toast.warn(error?.error || t('spcategorymaster.status_update_failed', 'Failed to update category status.'));
        }
    };

    // Handle save (create or update)
    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            if (editingCategory) {
                await dispatch(updateSpCategory({
                    id: editingCategory.spcategoryId,
                    spcategoryName: formData.spcategoryName.trim()
                })).unwrap();

                toast.success(t('spcategorymaster.updated_success', 'Category has been updated successfully.'));
                refreshList();
            } else {
                await dispatch(createSpCategory({
                    spcategoryName: formData.spcategoryName.trim()
                })).unwrap();

                toast.success(t('spcategorymaster.created_success', 'Category has been created successfully.'));
                setCurrentPage(1);
                refreshList(1);
            }

            setShowModal(false);
            setFormData({ spcategoryName: '' });
            setEditingCategory(null);
            setErrors({});
        } catch (error) {
            toast.warn(error?.error || (editingCategory ? t('spcategorymaster.update_failed', 'Failed to update category.') : t('spcategorymaster.create_failed', 'Failed to create category.')));
        }
    };

    // Handle modal close
    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({ spcategoryName: '' });
        setEditingCategory(null);
        setErrors({});
        dispatch(clearSpCategoryNameCheck());
    };

    // Handle view
    const handleView = (category) => {
        setViewingCategory(category);
        setShowViewModal(true);
    };

    // Handle close view modal
    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setViewingCategory(null);
    };

    // Bulk Upload Handlers
    const handleOpenBulkUpload = () => {
        setBulkUploadFile(null);
        setBulkUploadResult(null);
        setBulkFileError('');
        setBulkFormatValid(false);
        setBulkFormatChecking(false);
        setShowBulkUploadModal(true);
    };

    const handleCloseBulkUpload = () => {
        setShowBulkUploadModal(false);
        setBulkUploadFile(null);
        setBulkUploadResult(null);
        setBulkFileError('');
        setBulkFormatValid(false);
        setBulkFormatChecking(false);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        setBulkFileError('');
        setBulkFormatValid(false);
        setBulkUploadResult(null);
        if (!file) {
            setBulkUploadFile(null);
            return;
        }

        const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
        const allowedExtensions = ['.xlsx', '.xls'];
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!validTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
            setBulkFileError(t('spcategorymaster.only_excel_files', 'Only .xlsx and .xls files are allowed.'));
            setBulkUploadFile(null);
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setBulkFileError(t('spcategorymaster.max_file_size', 'Maximum file size is 5MB.'));
            setBulkUploadFile(null);
            return;
        }
        setBulkUploadFile(file);

        // Verify the file structure with the backend before allowing upload.
        setBulkFormatChecking(true);
        try {
            await dispatch(checkSpCategoryBulkFormat(file)).unwrap();
            setBulkFormatValid(true);
        } catch (err) {
            setBulkFormatValid(false);
            setBulkFileError(err?.error || t('spcategorymaster.invalid_file_format', 'Invalid Excel format'));
        } finally {
            setBulkFormatChecking(false);
        }
    };

    const handleDownloadSample = () => {
        dispatch(downloadSpCategorySampleExcel());
    };

    const handleBulkUpload = async () => {
        if (!bulkUploadFile) return;
        if (!bulkFormatValid) {
            setBulkFileError(t('spcategorymaster.fix_format_first', 'Please fix the file format errors before uploading.'));
            return;
        }

        try {
            const result = await dispatch(bulkUploadSpCategories(bulkUploadFile)).unwrap();
            setBulkUploadResult(result);
            refreshList();
        } catch (error) {
            toast.warn(error?.error || t('spcategorymaster.bulk_upload_failed', 'Failed to upload file.'));
        }
    };

    // Determine if a category is inactive
    const isInactiveCategory = (category) => {
        return category.status === false || category.status === 'false';
    };

    // Handle error state — only blank the page when the initial fetch fails
    // (no data yet). Per-action failures (delete/update/create/toggle) shouldn't
    // hijack the whole page; they're surfaced via toast/Swal in their handlers.
    const hasNoCategories = !spCategories || spCategories.length === 0;
    if (error && hasNoCategories && !spCategoryLoading) {
        if (error?.status >= 500 && error?.status <= 599) {
            return <ErrorPage error={error} pageName={t('spcategorymaster.page_name', 'Speciality Category Management')} />;
        }
        if (error?.message === 'Network Error') {
            return <ErrorPage error={error} pageName={t('spcategorymaster.page_name', 'Speciality Category Management')} />;
        }
        return <ErrorPage error={error} pageName={t('spcategorymaster.page_name', 'Speciality Category Management')} />;
    }

    return (
        <div className="w-full" data-testid="speciality-category-master-container">
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-4" data-testid="speciality-category-master-header">
                    <h1 className="text-2xl sm:text-3xl pb-2 font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2" data-testid="speciality-category-master-title">
                        {t('spcategorymaster.page_title', 'Speciality Category')}
                    </h1>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleAdd}
                            className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
                            data-testid="speciality-category-master-add-button"
                        >
                            <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" data-testid="speciality-category-master-add-icon" />
                            {t('spcategorymaster.add_category', 'Add Category')}
                        </button>
                                                <button
                            onClick={handleOpenBulkUpload}
                            className="group flex items-center gap-2 px-4 py-2 border-2 border-[#3AA8A8] text-[#3AA8A8] rounded-xl hover:bg-[#3AA8A8] hover:text-white transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer"
                            data-testid="speciality-category-master-bulk-upload-button"
                        >
                            <Upload size={20} className="group-hover:scale-110 transition-transform duration-300" data-testid="speciality-category-master-bulk-upload-icon" />
                            {t('spcategorymaster.bulk_upload', 'Bulk Upload')}
                        </button>
                    </div>
                </div>

                {/* Tab Bar + Search Row */}
                <div className="mb-5 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="speciality-category-master-tab-search-section">
                    <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2" data-testid="speciality-category-master-tabs">
                        <button
                            onClick={() => { setActiveTab('active'); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'active'
                                ? 'text-white'
                                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                }`}
                            data-testid="speciality-category-master-tab-active"
                        >
                            {activeTab === 'active' && (
                                <motion.div
                                    layoutId="activeSpCategoryStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20" data-testid="speciality-category-master-tab-active-text">{t('spcategorymaster.active', 'Active')}</span>
                        </button>

                        <button
                            onClick={() => { setActiveTab('inactive'); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'inactive'
                                ? 'text-white'
                                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                }`}
                            data-testid="speciality-category-master-tab-inactive"
                        >
                            {activeTab === 'inactive' && (
                                <motion.div
                                    layoutId="activeSpCategoryStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20" data-testid="speciality-category-master-tab-inactive-text">{t('spcategorymaster.inactive', 'Inactive')}</span>
                        </button>
                    </div>
                    <div className="relative group w-full sm:w-1/3" data-testid="speciality-category-master-search-container">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={22} data-testid="speciality-category-master-search-icon" />
                        <input
                            type="text"
                            placeholder={t('spcategorymaster.search_placeholder', 'Search categories...')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={50}
                            className={`w-full pl-12 pr-4 py-2 bg-gray-50 border-2 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400 ${searchError
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-200'
                                }`}
                            data-testid="speciality-category-master-search-input"
                        />
                        {searchError && (
                            <p className="text-red-600 text-xs mt-1 ml-1" data-testid="speciality-category-master-search-error">{searchError}</p>
                        )}
                    </div>
                </div>

                {/* Categories Grid */}
                {(!spCategoryLoading && filteredCategories.length === 0) ? (
                    <div className="text-center py-16 sm:py-20" data-testid="speciality-category-master-empty-container">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6">
                            <Layers size={48} className="text-gray-400" data-testid="speciality-category-master-empty-icon" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2" data-testid="speciality-category-master-empty-title">
                            {searchQuery ? t('spcategorymaster.no_categories_found', 'No categories found') : t('spcategorymaster.no_categories_yet', 'No categories yet')}
                        </h3>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto" data-testid="speciality-category-master-empty-text">
                            {searchQuery
                                ? t('spcategorymaster.try_adjusting_search', 'Try adjusting your search terms')
                                : t('spcategorymaster.create_first_category', 'Create your first speciality category to get started')}
                        </p>
                        {!searchQuery && activeTab === 'active' && (
                            <button
                                onClick={handleAdd}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                                data-testid="speciality-category-master-empty-create-button"
                            >
                                <Plus size={20} data-testid="speciality-category-master-empty-create-icon" />
                                {t('spcategorymaster.create_first_category_button', 'Create First Category')}
                            </button>
                        )}
                    </div>
                ) : spCategoryLoading ? (
                    <div className="flex items-center justify-center py-16 sm:py-20" data-testid="speciality-category-master-loading-container">
                        <div className="flex flex-col items-center gap-4">
                            <div className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-12 h-12 animate-spin" data-testid="speciality-category-master-loading-spinner"></div>
                            <p className="text-gray-500 text-sm" data-testid="speciality-category-master-loading-text">{t('spcategorymaster.loading', 'Loading categories...')}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-4" data-testid="speciality-category-master-grid">
                            {paginatedCategories.map((category) => {
                                const isInactive = isInactiveCategory(category);
                                const catId = category.spcategoryId || category._id;
                                return (
                                    <div
                                        key={catId}
                                        className={`group relative bg-white border rounded-lg p-4 overflow-hidden transition-all duration-300
                                        ${isInactive
                                                ? 'border-gray-200 opacity-75'
                                                : 'border-gray-200 hover:border-[#4ABDBD] hover:shadow-lg transform hover:-translate-y-0.5 cursor-pointer'
                                            }`}
                                        data-testid={`speciality-category-master-card-${catId}`}
                                    >
                                        {/* Gradient Accent */}
                                        {!isInactive && (
                                            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                                        )}

                                        {/* Toggle Switch */}
                                        <div className="absolute top-3 right-3 z-20">
                                            <div
                                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${isInactive
                                                    ? 'bg-gray-300'
                                                    : 'bg-[#4ABDBD]'
                                                    } ${spCategoryToggleLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                onClick={() => !spCategoryToggleLoading && handleStatusToggle(category)}
                                                data-testid={`speciality-category-master-toggle-${catId}`}
                                            >
                                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${isInactive
                                                    ? 'translate-x-1'
                                                    : 'translate-x-[18px]'
                                                    }`}></span>
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="mb-3 pr-12">
                                            <h3 className={`text-base font-bold line-clamp-1 mb-2 transition-colors
                                            ${isInactive ? 'text-gray-400' : 'text-gray-800 group-hover:text-[#4ABDBD]'}`}
                                                data-testid={`speciality-category-master-card-name-${catId}`}>
                                                <MasterLabel type="masterSpecialtyCategory" id={category.spcategoryId} field="spcategoryName" fallback={category.spcategoryName} />
                                            </h3>
                                            <p className={`text-xs leading-relaxed ${isInactive ? 'text-gray-400' : 'text-gray-500'}`}
                                                data-testid={`speciality-category-master-card-id-${catId}`}>
                                                ID: {category.spcategoryId}
                                            </p>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center justify-center gap-2 pt-3 border-t border-gray-100">
                                            <button
                                                onClick={() => handleView(category)}
                                                className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden"
                                                title={t('spcategorymaster.view_details', 'View Details')}
                                                data-testid={`speciality-category-master-view-button-${catId}`}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Eye className="h-4 w-4 relative z-10" data-testid={`speciality-category-master-view-icon-${catId}`} />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(category)}
                                                className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden"
                                                title={t('spcategorymaster.edit', 'Edit')}
                                                data-testid={`speciality-category-master-edit-button-${catId}`}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Edit2 className="h-4 w-4 relative z-10" data-testid={`speciality-category-master-edit-icon-${catId}`} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(category)}
                                                disabled={spCategoryDeleteLoading}
                                                className="group/btn text-red-500 hover:text-white p-2 rounded-lg bg-gradient-to-r from-red-50 to-red-50 hover:from-red-500 hover:to-red-600 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                                                title={t('spcategorymaster.delete', 'Delete')}
                                                data-testid={`speciality-category-master-delete-button-${catId}`}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Trash2 className="h-4 w-4 relative z-10" data-testid={`speciality-category-master-delete-icon-${catId}`} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Pagination */}
                        {spCategoryPagination.total > 0 && (
                            <Pagination
                                totalItems={spCategoryPagination.total}
                                currentPage={spCategoryPagination.page || currentPage}
                                itemsPerPage={spCategoryPagination.limit || itemsPerPage}
                                onPageChange={handlePageChange}
                                custombutton={paginatedCategories.length === 0 ? 'disable' : ''}
                            />
                        )}
                    </>
                )}

                {/* Create/Edit Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="speciality-category-master-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="speciality-category-master-modal-content">
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="speciality-category-master-modal-title">
                                    {editingCategory ? t('spcategorymaster.edit_category', 'Edit Category') : t('spcategorymaster.add_new_category', 'Add New Category')}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="speciality-category-master-modal-close-button"
                                >
                                    <X size={24} data-testid="speciality-category-master-modal-close-icon" />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-6" data-testid="speciality-category-master-form">
                                {/* Category Name */}
                                <div data-testid="speciality-category-master-name-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-category-master-name-label">
                                        {t('spcategorymaster.category_name_label', 'Category Name')}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="spcategoryName"
                                            value={formData.spcategoryName}
                                            onChange={handleInputChange}
                                            maxLength={MAX_NAME_LENGTH}
                                            className={`w-full px-4 py-2 pr-10 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.spcategoryName || (spCategoryNameCheckResult?.exists && formData.spcategoryName.trim())
                                                ? 'border-red-300 bg-red-50'
                                                : spCategoryNameCheckResult && !spCategoryNameCheckResult.exists && formData.spcategoryName.trim() && !spCategoryNameCheckLoading
                                                    ? 'border-green-300'
                                                    : 'border-gray-300'
                                                }`}
                                            placeholder={t('spcategorymaster.category_name_placeholder', 'Enter category name')}
                                            data-testid="speciality-category-master-name-input"
                                        />
                                        {formData.spcategoryName.trim() && !spCategoryNameCheckLoading && spCategoryNameCheckResult && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                {spCategoryNameCheckResult.exists ? (
                                                    <X className="h-5 w-5 text-red-500" data-testid="speciality-category-master-name-exists-icon" />
                                                ) : (
                                                    <CheckCircle2 className="h-5 w-5 text-green-500" data-testid="speciality-category-master-name-available-icon" />
                                                )}
                                            </div>
                                        )}
                                        {spCategoryNameCheckLoading && formData.spcategoryName.trim() && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                <div className="border-2 border-[#4ABDBD] border-t-transparent rounded-full w-5 h-5 animate-spin" data-testid="speciality-category-master-name-check-loading"></div>
                                            </div>
                                        )}
                                    </div>
                                    {errors.spcategoryName && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="speciality-category-master-name-error">{errors.spcategoryName}</p>
                                    )}
                                    {!errors.spcategoryName && spCategoryNameCheckResult?.exists && formData.spcategoryName.trim() && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="speciality-category-master-name-exists-error">
                                            {spCategoryNameCheckResult?.message || t('spcategorymaster.name_already_exists', 'This category name already exists')}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                                <button
                                    onClick={handleCloseModal}
                                    disabled={spCategoryCreateLoading || spCategoryUpdateLoading}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="speciality-category-master-modal-cancel-button"
                                >
                                    {t('spcategorymaster.cancel', 'Cancel')}
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={spCategoryCreateLoading || spCategoryUpdateLoading}
                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="speciality-category-master-modal-save-button"
                                >
                                    {(spCategoryCreateLoading || spCategoryUpdateLoading) && (
                                        <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" data-testid="speciality-category-master-modal-save-loading"></span>
                                    )}
                                    {spCategoryCreateLoading || spCategoryUpdateLoading
                                        ? (editingCategory ? t('spcategorymaster.updating', 'Updating...') : t('spcategorymaster.creating', 'Creating...'))
                                        : (editingCategory ? t('spcategorymaster.update', 'Update') : t('spcategorymaster.create', 'Create'))}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* View Modal */}
                {showViewModal && viewingCategory && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="speciality-category-master-view-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="speciality-category-master-view-modal-content">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="speciality-category-master-view-modal-title">
                                    {t('spcategorymaster.category_details', 'Category Details')}
                                </h2>
                                <button
                                    onClick={handleCloseViewModal}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="speciality-category-master-view-modal-close-button"
                                >
                                    <X size={24} data-testid="speciality-category-master-view-modal-close-icon" />
                                </button>
                            </div>

                            <div className="p-6 space-y-6">
                                <div data-testid="speciality-category-master-view-id-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-category-master-view-id-label">
                                        {t('spcategorymaster.category_id', 'Category ID')}
                                    </label>
                                    <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800 font-semibold" data-testid="speciality-category-master-view-id-value">
                                        {viewingCategory.spcategoryId}
                                    </div>
                                </div>

                                <div data-testid="speciality-category-master-view-name-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-category-master-view-name-label">
                                        {t('spcategorymaster.category_name', 'Category Name')}
                                    </label>
                                    <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800 font-semibold" data-testid="speciality-category-master-view-name-value">
                                        <MasterLabel type="masterSpecialtyCategory" id={viewingCategory.spcategoryId} field="spcategoryName" fallback={viewingCategory.spcategoryName} />
                                    </div>
                                </div>

                                <div data-testid="speciality-category-master-view-status-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-category-master-view-status-label">
                                        {t('spcategorymaster.status_label', 'Status')}
                                    </label>
                                    <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50">
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${(viewingCategory.status === true || viewingCategory.status === 'true')
                                            ? 'bg-green-100 text-green-700 border border-green-300'
                                            : 'bg-gray-200 text-gray-500 border border-gray-300'
                                            }`}
                                            data-testid="speciality-category-master-view-status-value">
                                            {(viewingCategory.status === true || viewingCategory.status === 'true') ? t('spcategorymaster.active', 'Active') : t('spcategorymaster.inactive', 'Inactive')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
                                <button
                                    onClick={handleCloseViewModal}
                                    className="px-6 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md"
                                    data-testid="speciality-category-master-view-modal-close-footer-button"
                                >
                                    {t('spcategorymaster.close', 'Close')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bulk Upload Modal */}
                {showBulkUploadModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="speciality-category-master-bulk-upload-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="speciality-category-master-bulk-upload-modal-content">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="speciality-category-master-bulk-upload-modal-title">
                                    {t('spcategorymaster.bulk_upload_title', 'Bulk Upload Categories')}
                                </h2>
                                <button
                                    onClick={handleCloseBulkUpload}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="speciality-category-master-bulk-upload-modal-close-button"
                                >
                                    <X size={24} data-testid="speciality-category-master-bulk-upload-modal-close-icon" />
                                </button>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Download Sample */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="speciality-category-master-download-sample-section">
                                    <div className="flex items-start gap-3">
                                        <FileSpreadsheet size={24} className="text-blue-500 mt-0.5" data-testid="speciality-category-master-download-sample-icon" />
                                        <div className="flex-1">
                                            <h3 className="text-sm font-semibold text-blue-800 mb-1" data-testid="speciality-category-master-download-sample-title">
                                                {t('spcategorymaster.download_sample_title', 'Download Sample Template')}
                                            </h3>
                                            <p className="text-xs text-blue-600 mb-2" data-testid="speciality-category-master-download-sample-description">
                                                {t('spcategorymaster.download_sample_desc', 'Download the sample Excel template with the correct format before uploading.')}
                                            </p>
                                            <button
                                                onClick={handleDownloadSample}
                                                disabled={spCategoryDownloadLoading}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                data-testid="speciality-category-master-download-template-button"
                                            >
                                                {spCategoryDownloadLoading ? (
                                                    <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" data-testid="speciality-category-master-download-template-loading"></span>
                                                ) : (
                                                    <Download size={16} data-testid="speciality-category-master-download-template-icon" />
                                                )}
                                                {t('spcategorymaster.download_template', 'Download Template')}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* File Upload */}
                                <div data-testid="speciality-category-master-file-upload-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="speciality-category-master-file-upload-label">
                                        {t('spcategorymaster.select_file', 'Select Excel File')}
                                    </label>
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#4ABDBD] transition-colors"
                                        data-testid="speciality-category-master-file-dropzone"
                                    >
                                        <Upload size={32} className="mx-auto text-gray-400 mb-2" data-testid="speciality-category-master-file-dropzone-icon" />
                                        <p className="text-sm text-gray-600" data-testid="speciality-category-master-file-dropzone-text">
                                            {bulkUploadFile
                                                ? bulkUploadFile.name
                                                : t('spcategorymaster.click_to_select', 'Click to select an Excel file (.xlsx, .xls)')}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1" data-testid="speciality-category-master-file-dropzone-hint">
                                            {t('spcategorymaster.max_size', 'Maximum file size: 5MB')}
                                        </p>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        data-testid="speciality-category-master-file-input"
                                    />
                                    {bulkFormatChecking && (
                                        <p className="mt-2 text-xs text-gray-500 flex items-center gap-1" data-testid="speciality-category-master-format-checking">
                                            <span className="border-2 border-gray-400 border-t-transparent rounded-full w-3 h-3 animate-spin"></span>
                                            {t('spcategorymaster.checking_format', 'Checking file format...')}
                                        </p>
                                    )}
                                    {!bulkFormatChecking && bulkUploadFile && bulkFormatValid && !bulkFileError && (
                                        <p className="mt-2 text-xs text-green-600 flex items-center gap-1" data-testid="speciality-category-master-format-valid">
                                            <CheckCircle size={12} />
                                            {t('spcategorymaster.format_valid', 'File format looks good')}
                                        </p>
                                    )}
                                    {bulkFileError && (
                                        <p className="mt-2 text-sm text-red-500 flex items-center gap-1" data-testid="speciality-category-master-file-error">
                                            <AlertCircle size={14} />
                                            {bulkFileError}
                                        </p>
                                    )}
                                </div>

                                {/* Upload Result */}
                                {bulkUploadResult && (
                                    <div className="space-y-3" data-testid="speciality-category-master-upload-result-container">
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="speciality-category-master-upload-summary-section">
                                            <div className="flex items-center gap-2 mb-2">
                                                <CheckCircle size={18} className="text-green-500" data-testid="speciality-category-master-upload-summary-icon" />
                                                <span className="text-sm font-semibold text-green-800" data-testid="speciality-category-master-upload-summary-title">
                                                    {t('spcategorymaster.upload_complete', 'Upload Complete')}
                                                </span>
                                            </div>
                                            <div className="text-xs text-green-700 space-y-1">
                                                <p data-testid="speciality-category-master-created-count-text">{t('spcategorymaster.created_count', { count: bulkUploadResult.summary?.totalCreated || 0, defaultValue: `Created: ${bulkUploadResult.summary?.totalCreated || 0}` })}</p>
                                                <p data-testid="speciality-category-master-duplicates-count-text">{t('spcategorymaster.duplicates_count', { count: bulkUploadResult.summary?.totalDuplicates || 0, defaultValue: `Duplicates skipped: ${bulkUploadResult.summary?.totalDuplicates || 0}` })}</p>
                                                <p data-testid="speciality-category-master-errors-count-text">{t('spcategorymaster.errors_count', { count: bulkUploadResult.summary?.totalErrors || 0, defaultValue: `Errors: ${bulkUploadResult.summary?.totalErrors || 0}` })}</p>
                                            </div>
                                        </div>

                                        {bulkUploadResult.duplicates?.length > 0 && (
                                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="speciality-category-master-duplicates-section">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <AlertCircle size={18} className="text-amber-500" data-testid="speciality-category-master-duplicates-icon" />
                                                    <span className="text-sm font-semibold text-amber-800" data-testid="speciality-category-master-duplicates-title">
                                                        {t('spcategorymaster.duplicates_found', 'Duplicates Found')}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-amber-700 space-y-1 max-h-24 overflow-y-auto">
                                                    {bulkUploadResult.duplicates.map((dup, idx) => (
                                                        <p key={idx} data-testid={`speciality-category-master-duplicate-row-${idx}`}>{t('spcategorymaster.row', 'Row')} {dup.row}: {dup.spcategoryName} ({t('spcategorymaster.existing', 'existing')}: {dup.existingId})</p>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {bulkUploadResult.errors?.length > 0 && (
                                            <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="speciality-category-master-errors-section">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <AlertCircle size={18} className="text-red-500" data-testid="speciality-category-master-errors-icon" />
                                                    <span className="text-sm font-semibold text-red-800" data-testid="speciality-category-master-errors-title">
                                                        {t('spcategorymaster.errors_found', 'Errors Found')}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-red-700 space-y-1 max-h-24 overflow-y-auto">
                                                    {bulkUploadResult.errors.map((err, idx) => (
                                                        <p key={idx} data-testid={`speciality-category-master-error-row-${idx}`}>{t('spcategorymaster.row', 'Row')} {err.row}: {err.error}</p>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                                <button
                                    onClick={handleCloseBulkUpload}
                                    disabled={spCategoryBulkUploadLoading}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="speciality-category-master-bulk-upload-modal-close-footer-button"
                                >
                                    {t('spcategorymaster.close', 'Close')}
                                </button>
                                {!bulkUploadResult && (
                                    <button
                                        onClick={handleBulkUpload}
                                        disabled={!bulkUploadFile || spCategoryBulkUploadLoading || bulkFormatChecking || !bulkFormatValid}
                                        className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        data-testid="speciality-category-master-bulk-upload-submit-button"
                                    >
                                        {spCategoryBulkUploadLoading && (
                                            <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" data-testid="speciality-category-master-bulk-upload-submit-loading"></span>
                                        )}
                                        {spCategoryBulkUploadLoading
                                            ? t('spcategorymaster.uploading', 'Uploading...')
                                            : t('spcategorymaster.upload', 'Upload')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SpecialityCategory;
